import { useState } from "react";
import { useAllMarkets } from "@/hooks/useMarkets";
import MarketCard from "./MarketCard";
import { categorize, CATEGORIES, type Category } from "@/lib/categories";

type FilterType = "all" | "open" | "resolved";

export default function MarketList() {
  const { markets, isLoading, error } = useAllMarkets();
  const [filter, setFilter] = useState<FilterType>("all");
  const [category, setCategory] = useState<Category | "All">("All");

  const filteredMarkets = markets.filter((market) => {
    const statusMatch =
      filter === "open" ? market.isOpen :
      filter === "resolved" ? market.isResolved :
      true;
    const categoryMatch =
      category === "All" || categorize(market.question) === category;
    return statusMatch && categoryMatch;
  });

  // Sort by end time (soonest first for open, most recent for resolved)
  const sortedMarkets = [...filteredMarkets].sort((a, b) => {
    if (filter === "resolved") {
      return Number(b.endTime) - Number(a.endTime);
    }
    return Number(a.endTime) - Number(b.endTime);
  });

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {[1, 2, 3, 4, 5, 6].map((i) => (
          <div key={i} className="card animate-pulse">
            <div className="p-5">
              <div className="h-4 bg-gray-200 rounded w-20 mb-3"></div>
              <div className="h-6 bg-gray-200 rounded w-full mb-2"></div>
              <div className="h-6 bg-gray-200 rounded w-3/4 mb-4"></div>
              <div className="flex gap-3 mb-4">
                <div className="flex-1 h-16 bg-gray-200 rounded-lg"></div>
                <div className="flex-1 h-16 bg-gray-200 rounded-lg"></div>
              </div>
              <div className="h-2 bg-gray-200 rounded-full mb-3"></div>
              <div className="h-4 bg-gray-200 rounded w-32 mx-auto"></div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="card p-8 text-center">
        <div className="text-danger mb-2">Error loading markets</div>
        <p className="text-gray-600 text-sm">
          Make sure you're connected to the correct network and the contracts are deployed.
        </p>
      </div>
    );
  }

  return (
    <div>
      {/* Category Filter Tabs */}
      <div className="flex flex-wrap gap-2 mb-4">
        {(["All", ...CATEGORIES] as const).map((c) => (
          <button
            key={c}
            onClick={() => setCategory(c)}
            aria-pressed={category === c}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
              category === c
                ? "bg-primary-600 text-white"
                : "bg-white border border-gray-200 text-ink/70 hover:bg-gray-50"
            }`}
          >
            {c}
          </button>
        ))}
      </div>

      {/* Status Filter Tabs */}
      <div className="flex gap-2 mb-6">
        {(["all", "open", "resolved"] as FilterType[]).map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            aria-pressed={filter === f}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              filter === f
                ? "bg-primary-600 text-white"
                : "bg-white text-gray-600 hover:bg-gray-100 border border-gray-200"
            }`}
          >
            {f.charAt(0).toUpperCase() + f.slice(1)}
            <span className="ml-1 text-xs opacity-75">
              ({markets.filter((m) => {
                const statusOk = f === "open" ? m.isOpen : f === "resolved" ? m.isResolved : true;
                const catOk = category === "All" || categorize(m.question) === category;
                return statusOk && catOk;
              }).length})
            </span>
          </button>
        ))}
      </div>

      {/* Markets Grid */}
      {sortedMarkets.length === 0 ? (
        <div className="card p-8 text-center">
          <div className="text-gray-500 mb-2">No markets found</div>
          <p className="text-gray-400 text-sm">
            {filter === "all"
              ? "Be the first to create a prediction market!"
              : `No ${filter} markets at the moment.`}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {sortedMarkets.map((market) => (
            <MarketCard key={market.address} {...market} />
          ))}
        </div>
      )}
    </div>
  );
}
